import React, { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes, faTag } from '@fortawesome/free-solid-svg-icons';
import '../styles/TagInput.css';

function TagInput({ value, onChange, placeholder = "Add tags...", maxTags = 20 }) {
  const [inputValue, setInputValue] = useState('');
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [selectedIndex, setSelectedIndex] = useState(-1);
  const [allTags, setAllTags] = useState([]);
  const [tagCounts, setTagCounts] = useState({});
  const inputRef = useRef(null);
  const suggestionsRef = useRef(null);

  // Parse tags from string value
  const tags = value ? value.split(',').map(tag => tag.trim()).filter(tag => tag.length > 0) : [];

  // Fetch all available tags with counts on mount
  useEffect(() => {
    fetch(`${process.env.REACT_APP_API_URL}/api/tags-with-counts`, {
      credentials: 'include',
    })
      .then(response => response.json())
      .then(data => {
        if (data.tagCounts) {
          setTagCounts(data.tagCounts);
          // Sort tags by count (descending)
          const sortedTags = Object.keys(data.tagCounts).sort((a, b) => 
            data.tagCounts[b] - data.tagCounts[a]
          );
          setAllTags(sortedTags);
        }
      })
      .catch(error => {
        console.error('Error fetching tags:', error);
        // Fallback to regular tags endpoint
        fetch(`${process.env.REACT_APP_API_URL}/api/tags`, {
          credentials: 'include',
        })
          .then(response => response.json())
          .then(data => {
            setAllTags(data.tags || []);
          })
          .catch(err => console.error('Error fetching tags:', err));
      });
  }, []);

  // Filter suggestions based on input
  useEffect(() => {
    const normalizedTags = tags.map(t => t.toLowerCase());
    
    if (inputValue.trim()) {
      const normalizedInput = inputValue.trim().toLowerCase();
      const filtered = allTags
        .filter(tag => 
          tag.toLowerCase().includes(normalizedInput) && 
          !normalizedTags.includes(tag.toLowerCase())
        )
        .slice(0, 10); // Limit to 10 suggestions
      setSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } else {
      // Show all available tags when input is empty (focus mode)
      const availableTags = allTags
        .filter(tag => !normalizedTags.includes(tag.toLowerCase()))
        .slice(0, 10); // Limit to 10 suggestions
      setSuggestions(availableTags);
      setShowSuggestions(false); // Will be shown on focus
    }
  }, [inputValue, allTags, tags.length]); // Use tags.length instead of tags to avoid dependency issues

  // Handle click outside to close suggestions
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        suggestionsRef.current && 
        !suggestionsRef.current.contains(event.target) &&
        inputRef.current &&
        !inputRef.current.contains(event.target)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const addTag = (tag) => {
    const normalizedTag = tag.trim().toLowerCase();
    if (normalizedTag && !tags.map(t => t.toLowerCase()).includes(normalizedTag) && tags.length < maxTags) {
      const newTags = [...tags, normalizedTag];
      onChange(newTags.join(', '));
      setInputValue('');
      setShowSuggestions(false);
      setSelectedIndex(-1);
    }
  };

  const removeTag = (index) => {
    const newTags = tags.filter((_, i) => i !== index);
    onChange(newTags.join(', '));
  };

  const handleInputChange = (e) => {
    const value = e.target.value;
    
    // Check for comma or Enter to add tag
    if (value.includes(',')) {
      const parts = value.split(',');
      parts.forEach((part, index) => {
        if (index < parts.length - 1) {
          addTag(part);
        } else {
          setInputValue(part);
        }
      });
    } else {
      setInputValue(value);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      if (selectedIndex >= 0 && selectedIndex < suggestions.length) {
        addTag(suggestions[selectedIndex]);
      } else if (inputValue.trim()) {
        addTag(inputValue);
      }
    } else if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex(prev => 
        prev < suggestions.length - 1 ? prev + 1 : prev
      );
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex(prev => prev > 0 ? prev - 1 : -1);
    } else if (e.key === 'Escape') {
      setShowSuggestions(false);
      setSelectedIndex(-1);
    } else if (e.key === 'Backspace' && !inputValue && tags.length > 0) {
      // Remove last tag if backspace on empty input
      removeTag(tags.length - 1);
    }
  };

  const handleSuggestionClick = (suggestion) => {
    addTag(suggestion);
  };

  return (
    <div className="tag-input-container">
      <div className="tag-input-wrapper">
        <div className="tags-display">
          {tags.map((tag, index) => (
            <span key={index} className="tag">
              <FontAwesomeIcon icon={faTag} className="tag-icon" />
              {tag}
              <button
                type="button"
                className="tag-remove"
                onClick={() => removeTag(index)}
                aria-label={`Remove tag ${tag}`}
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </span>
          ))}
          <input
            ref={inputRef}
            type="text"
            className="tag-input"
            value={inputValue}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onFocus={() => {
              if (suggestions.length > 0) {
                setShowSuggestions(true);
              }
            }}
            placeholder={tags.length === 0 ? placeholder : ''}
            disabled={tags.length >= maxTags}
          />
        </div>
      </div>
      
      {showSuggestions && (
        <div ref={suggestionsRef} className="tag-suggestions">
          {suggestions.map((suggestion, index) => (
            <div
              key={suggestion}
              className={`tag-suggestion ${index === selectedIndex ? 'selected' : ''}`}
              onClick={() => handleSuggestionClick(suggestion)}
              onMouseEnter={() => setSelectedIndex(index)}
            >
              <FontAwesomeIcon icon={faTag} className="suggestion-icon" />
              <span className="suggestion-text">{suggestion}</span>
              {tagCounts[suggestion] > 0 && (
                <span className="tag-count">({tagCounts[suggestion]})</span>
              )}
            </div>
          ))}
        </div>
      )}
      
      {tags.length >= maxTags && (
        <div className="tag-limit-message">
          Maximum of {maxTags} tags reached
        </div>
      )}
    </div>
  );
}

export default TagInput;